/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   InfrastructureTrendsPage.tsx
 *     Description     :   Infrastructure trends visualization dashboard
 *     Author          :   Peter Dobler
 *     Created         :   2025-01-21
 *     Modified        :   2026-06-14 | Conformed to Spec 197 design blueprint —
 *                         migrated off MUI (Container/Paper/Grid/Table/Chip/Tabs/
 *                         ToggleButton/Select) onto the canonical token UI kit
 *                         (PageLayout/Card/DataTable/StatCard/Badge/Button/Select/
 *                         token tabs). Presentation-only; data/logic unchanged.
 *                         AnomalyList + ExportDialog (non-target widgets) wrapped
 *                         but otherwise untouched.
 *     Constitution    :   v1.19.0
 *     Feature         :   021-infrastructure-data-collection (T028, T032-T034)
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  TrendingUp,
  HardDrive,
  Gauge,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Download,
} from 'lucide-react';
import AnomalyList, { type Anomaly } from './AnomalyList';
import ExportDialog from './ExportDialog';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Select,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import * as snapshotsApi from '../../services/snapshotsApi';
import type { TimeRange, CapacityType } from '../../services/snapshotsApi';

// ============================================================================
// Types
// ============================================================================

type TrendTab = 'capacity' | 'storage' | 'pipeline';
type CapacityTypeFilter = 'all' | CapacityType;
type StatStatus = 'ok' | 'warning' | 'critical';

const STAT_STATUS_CLASS: Record<StatStatus, string> = {
  ok: 'text-[var(--color-status-success)]',
  warning: 'text-[var(--color-status-warning)]',
  critical: 'text-[var(--color-status-danger)]',
};

// ============================================================================
// Capacity Utilization Tab
// ============================================================================

interface CapacityUtilizationTabProps {
  timeRange: TimeRange;
  capacityType: CapacityTypeFilter;
}

const CapacityUtilizationTab: React.FC<CapacityUtilizationTabProps> = ({
  timeRange,
  capacityType,
}) => {
  const [data, setData] = useState<snapshotsApi.CapacityUtilizationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await snapshotsApi.getCapacityUtilizationTrends({
        time_range: timeRange,
        capacity_type: capacityType === 'all' ? undefined : capacityType,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [timeRange, capacityType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <LoadingState rows={6} />;
  }

  if (error) {
    return <ErrorState title="Failed to load capacity data" message={error} onRetry={fetchData} />;
  }

  if (data && data.count === 0 && data.not_measured?.length) {
    return (
      <EmptyState
        title="No utilization telemetry in this period"
        message={
          data.not_measured.length === 1
            ? `${notMeasuredNames(data)} has no utilization telemetry in this period`
            : `None of the ${capacityCount(data.not_measured.length)} has utilization telemetry in this period: ${notMeasuredNames(data)}`
        }
        icon={<Gauge className="h-10 w-10" />}
      />
    );
  }

  if (!data || data.count === 0) {
    return (
      <EmptyState
        title="No Capacity Utilization Data"
        message="Capacity utilization snapshots have not been collected yet. Data will appear after the first daily collection run."
        icon={<Gauge className="h-10 w-10" />}
      />
    );
  }

  return <CapacityUtilizationTabInner data={data} />;
};

function capacityCount(n: number): string {
  return `${n} ${n === 1 ? 'capacity' : 'capacities'}`;
}

function notMeasuredNames(data: snapshotsApi.CapacityUtilizationResponse): string {
  return data.not_measured.map((c) => c.capacity_name).join(', ');
}

const CapacityUtilizationTabInner: React.FC<{
  data: snapshotsApi.CapacityUtilizationResponse;
}> = ({ data }) => {
  const columns: DataTableColumn<snapshotsApi.CapacityUtilizationTrend>[] = [
    {
      key: 'item_name',
      header: 'Capacity',
      width: '22%',
      render: (c) => (
        <span className="font-medium text-[var(--color-text-primary)]">{c.item_name}</span>
      ),
    },
    {
      key: 'capacity_type',
      header: 'Type',
      width: '14%',
      render: (c) => (
        <Badge variant="secondary" isOutlined>
          {c.capacity_type}
        </Badge>
      ),
    },
    { key: 'sku', header: 'SKU', width: '10%' },
    {
      key: 'latest_value',
      header: 'Current',
      align: 'right',
      width: '10%',
      render: (c) => `${c.latest_value.toFixed(1)}%`,
    },
    {
      key: 'avg_value',
      header: 'Avg',
      align: 'right',
      width: '9%',
      render: (c) => `${c.avg_value.toFixed(1)}%`,
    },
    {
      key: 'max_value',
      header: 'Max',
      align: 'right',
      width: '9%',
      render: (c) => `${c.max_value.toFixed(1)}%`,
    },
    {
      key: 'change_percent',
      header: 'Change',
      align: 'right',
      width: '11%',
      // Spec 298 FR-004: a series starting at 0 has no percentage change; it reads "—", never "+0.0%"
      render: (c) => c.change_percent === null ? (
        <span className="text-[var(--color-text-secondary)]" title="The first measured point is 0, so there is no percentage change from it">—</span>
      ) : (
        <span
          className={
            c.change_percent >= 0
              ? 'text-[var(--color-status-success)]'
              : 'text-[var(--color-status-danger)]'
          }
        >
          {c.change_percent >= 0 ? '+' : ''}
          {c.change_percent.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '15%',
      render: (c) => {
        const status = snapshotsApi.getUtilizationStatus(c.latest_value);
        if (status === 'critical') {
          return (
            <Badge variant="error" leftIcon={<XCircle className="h-3 w-3" />}>
              Critical
            </Badge>
          );
        }
        if (status === 'warning') {
          return (
            <Badge variant="warning" leftIcon={<AlertTriangle className="h-3 w-3" />}>
              Warning
            </Badge>
          );
        }
        return (
          <Badge variant="success" leftIcon={<CheckCircle2 className="h-3 w-3" />}>
            OK
          </Badge>
        );
      },
    },
  ];

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Average Utilization"
          value={`${data.summary.avg_utilization.toFixed(1)}%`}
          valueClassName={STAT_STATUS_CLASS[snapshotsApi.getUtilizationStatus(data.summary.avg_utilization)]}
          icon={<Gauge className="h-4 w-4" />}
        />
        <StatCard
          label="Max Utilization"
          value={`${data.summary.max_utilization.toFixed(1)}%`}
          valueClassName={STAT_STATUS_CLASS[snapshotsApi.getUtilizationStatus(data.summary.max_utilization)]}
          icon={<BarChart3 className="h-4 w-4" />}
        />
        <StatCard
          label="Capacities >80%"
          value={data.summary.over_80_percent_count}
          subtext={`of ${data.count} measured`}
          valueClassName={data.summary.over_80_percent_count > 0 ? STAT_STATUS_CLASS.warning : undefined}
          icon={<AlertTriangle className="h-4 w-4" />}
        />
        <StatCard
          label="Measured capacities"
          value={data.count}
          subtext={data.not_measured?.length ? `${data.not_measured.length} not measured` : undefined}
          icon={<TrendingUp className="h-4 w-4" />}
        />
      </div>

      <Card padding="none">
        <DataTable
          columns={columns}
          rows={data.capacities}
          rowKey={(c) => c.item_id}
          minWidth="64rem"
          emptyMessage="No capacities found"
        />
      </Card>
      {data.not_measured?.length > 0 && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
          {`No utilization telemetry in this period for ${capacityCount(data.not_measured.length)}: ${notMeasuredNames(data)}`}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Lakehouse Storage Tab
// ============================================================================

const LakehouseStorageTab: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const [data, setData] = useState<snapshotsApi.LakehouseStorageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await snapshotsApi.getLakehouseStorageTrends({ time_range: timeRange });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <LoadingState rows={6} />;
  }

  if (error) {
    return <ErrorState title="Failed to load lakehouse data" message={error} onRetry={fetchData} />;
  }

  if (!data || data.count === 0) {
    return (
      <EmptyState
        title="No Lakehouse Storage Data"
        message="Lakehouse storage snapshots have not been collected yet. Data will appear after the first daily collection run."
        icon={<HardDrive className="h-10 w-10" />}
      />
    );
  }

  return <LakehouseStorageTabInner data={data} />;
};

const LakehouseStorageTabInner: React.FC<{
  data: snapshotsApi.LakehouseStorageResponse;
}> = ({ data }) => {
  const columns: DataTableColumn<snapshotsApi.LakehouseStorageTrend>[] = [
    {
      key: 'lakehouse_name',
      header: 'Lakehouse',
      width: '24%',
      render: (l) => (
        <span className="font-medium text-[var(--color-text-primary)]">{l.lakehouse_name}</span>
      ),
    },
    { key: 'workspace_name', header: 'Workspace', width: '22%' },
    {
      key: 'latest_value',
      header: 'Current Size',
      align: 'right',
      width: '14%',
      render: (l) => snapshotsApi.formatStorageSize(l.latest_value),
    },
    {
      key: 'growth_rate_daily_gb',
      header: 'Daily Growth',
      align: 'right',
      width: '14%',
      render: (l) => snapshotsApi.formatStorageSize(l.growth_rate_daily_gb),
    },
    {
      key: 'change_percent',
      header: 'Growth %',
      align: 'right',
      width: '12%',
      // Spec 298 FR-004: as the capacity column, "—" over a series starting at 0
      render: (l) => l.change_percent === null ? (
        <span className="text-[var(--color-text-secondary)]" title="The first measured point is 0, so there is no percentage change from it">—</span>
      ) : (
        <span
          className={
            l.change_percent > 20
              ? 'text-[var(--color-status-warning)]'
              : 'text-[var(--color-text-primary)]'
          }
        >
          {l.change_percent >= 0 ? '+' : ''}
          {l.change_percent.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '14%',
      // No growth status without a percentage change (the series starts at 0)
      render: (l) =>
        l.change_percent === null ? (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ) : l.change_percent > 20 ? (
          <Badge variant="warning" leftIcon={<AlertTriangle className="h-3 w-3" />}>
            High Growth
          </Badge>
        ) : (
          <Badge variant="success" isOutlined>
            Normal
          </Badge>
        ),
    },
  ];

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total Storage"
          value={snapshotsApi.formatStorageSize(data.summary.total_storage_gb)}
          icon={<HardDrive className="h-4 w-4" />}
        />
        <StatCard
          label="Avg Daily Growth"
          value={snapshotsApi.formatStorageSize(data.summary.avg_daily_growth_gb)}
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <StatCard
          label="High Growth Items"
          value={data.summary.high_growth_count}
          subtext=">20% growth"
          valueClassName={data.summary.high_growth_count > 0 ? STAT_STATUS_CLASS.warning : undefined}
          icon={<AlertTriangle className="h-4 w-4" />}
        />
        <StatCard
          label="Total Lakehouses"
          value={data.count}
          icon={<BarChart3 className="h-4 w-4" />}
        />
      </div>

      <Card padding="none">
        <DataTable
          columns={columns}
          rows={data.lakehouses}
          rowKey={(l) => l.item_id}
          minWidth="52rem"
          emptyMessage="No lakehouses found"
        />
      </Card>
    </div>
  );
};

// ============================================================================
// Pipeline Health Tab
// ============================================================================

const PipelineHealthTab: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const [data, setData] = useState<snapshotsApi.PipelineHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await snapshotsApi.getPipelineHealthTrends({ time_range: timeRange });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <LoadingState rows={6} />;
  }

  if (error) {
    return <ErrorState title="Failed to load pipeline data" message={error} onRetry={fetchData} />;
  }

  if (!data || data.count === 0) {
    return (
      <EmptyState
        title="No Pipeline Health Data"
        message="Pipeline health snapshots have not been collected yet. Data will appear after the first daily collection run."
        icon={<TrendingUp className="h-10 w-10" />}
      />
    );
  }

  return <PipelineHealthTabInner data={data} />;
};

const PipelineHealthTabInner: React.FC<{
  data: snapshotsApi.PipelineHealthResponse;
}> = ({ data }) => {
  const columns: DataTableColumn<snapshotsApi.PipelineHealthTrend>[] = [
    {
      key: 'pipeline_name',
      header: 'Pipeline',
      width: '24%',
      render: (p) => (
        <span className="font-medium text-[var(--color-text-primary)]">{p.pipeline_name}</span>
      ),
    },
    { key: 'workspace_name', header: 'Workspace', width: '20%' },
    { key: 'total_runs', header: 'Total Runs', align: 'right', width: '11%' },
    { key: 'success_count', header: 'Success', align: 'right', width: '10%' },
    {
      key: 'failure_count',
      header: 'Failures',
      align: 'right',
      width: '10%',
      render: (p) => (
        <span
          className={
            p.failure_count > 0
              ? 'text-[var(--color-status-danger)]'
              : 'text-[var(--color-text-primary)]'
          }
        >
          {p.failure_count}
        </span>
      ),
    },
    {
      key: 'avg_success_rate',
      header: 'Success Rate',
      align: 'right',
      width: '12%',
      // Spec 298 FR-004: a pipeline with no measured day has no rate; it says so, never "0.0%"
      render: (p) => p.avg_success_rate === null ? (
        <span className="text-[var(--color-text-secondary)]">no runs</span>
      ) : (
        <span
          className={clsx(
            'font-medium',
            p.avg_success_rate < 80
              ? 'text-[var(--color-status-warning)]'
              : 'text-[var(--color-status-success)]',
          )}
        >
          {p.avg_success_rate.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '13%',
      render: (p) =>
        p.avg_success_rate === null ? (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ) : p.avg_success_rate < 80 ? (
          <Badge variant="warning" leftIcon={<AlertTriangle className="h-3 w-3" />}>
            Low Success
          </Badge>
        ) : (
          <Badge variant="success" leftIcon={<CheckCircle2 className="h-3 w-3" />}>
            Healthy
          </Badge>
        ),
    },
  ];

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Avg Success Rate"
          value={data.summary.avg_success_rate === null ? 'no runs' : `${data.summary.avg_success_rate.toFixed(1)}%`}
          valueClassName={data.summary.avg_success_rate !== null && data.summary.avg_success_rate < 80 ? STAT_STATUS_CLASS.warning : undefined}
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
        <StatCard
          label="Total Runs"
          value={data.summary.total_runs.toLocaleString()}
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <StatCard
          label="Total Failures"
          value={data.summary.total_failures.toLocaleString()}
          valueClassName={data.summary.total_failures > 0 ? STAT_STATUS_CLASS.warning : undefined}
          icon={<XCircle className="h-4 w-4" />}
        />
        <StatCard
          label="Low Success Pipelines"
          value={data.summary.low_success_count}
          subtext="<80% success"
          valueClassName={data.summary.low_success_count > 0 ? STAT_STATUS_CLASS.warning : undefined}
          icon={<AlertTriangle className="h-4 w-4" />}
        />
      </div>

      <Card padding="none">
        <DataTable
          columns={columns}
          rows={data.pipelines}
          rowKey={(p) => p.item_id}
          minWidth="52rem"
          emptyMessage="No pipelines found"
        />
      </Card>
    </div>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 365, label: '1 Year' },
];

const TABS: { value: TrendTab; label: string; icon: React.ReactNode }[] = [
  { value: 'capacity', label: 'Capacity Utilization', icon: <Gauge className="h-4 w-4" /> },
  { value: 'storage', label: 'Lakehouse Storage', icon: <HardDrive className="h-4 w-4" /> },
  { value: 'pipeline', label: 'Pipeline Health', icon: <TrendingUp className="h-4 w-4" /> },
];

export const InfrastructureTrendsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TrendTab>('capacity');
  const [timeRange, setTimeRange] = useState<TimeRange>(30);
  const [capacityTypeFilter, setCapacityTypeFilter] = useState<CapacityTypeFilter>('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <PageLayout
      helpId="auto.infrastructure-trends"
      title="Infrastructure Trends"
      description="Monitor capacity utilization, storage growth, and pipeline health over time"
      icon={<TrendingUp className="h-6 w-6" />}
      actions={
        <>
          <Button
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export
          </Button>
          <Button variant="outline" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={handleRefresh}>
            Refresh
          </Button>
        </>
      }
    >
      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--color-text-primary)]">
              Time Range
            </label>
            <div className="inline-flex overflow-hidden rounded-md border border-[var(--color-border)]">
              {TIME_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTimeRange(opt.value)}
                  className={clsx(
                    'border-r border-[var(--color-border)] px-3 py-1.5 text-sm font-medium transition-colors last:border-r-0',
                    timeRange === opt.value
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {activeTab === 'capacity' && (
            <Select
              isFullWidth
              label="Capacity Type"
              value={capacityTypeFilter}
              onChange={(e) => setCapacityTypeFilter(e.target.value as CapacityTypeFilter)}
              options={[{ value: 'all', label: 'All Types' }, ...snapshotsApi.CAPACITY_TYPE_OPTIONS]}
            />
          )}
        </div>
      </Card>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveTab(tab.value)}
            className={clsx(
              '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.value
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      <div key={refreshKey} className="mb-6">
        {activeTab === 'capacity' && (
          <CapacityUtilizationTab timeRange={timeRange} capacityType={capacityTypeFilter} />
        )}
        {activeTab === 'storage' && <LakehouseStorageTab timeRange={timeRange} />}
        {activeTab === 'pipeline' && <PipelineHealthTab timeRange={timeRange} />}
      </div>

      {/* Anomalies Section (T044: US3 - Change Detection) */}
      <AnomaliesSection key={`anomalies-${refreshKey}`} />
      </Extended>

      {/* Export Dialog (T059-T061: US5 - Export) */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        initialCapacityType={capacityTypeFilter !== 'all' ? capacityTypeFilter : undefined}
        initialTimeRange={timeRange}
      />
    </PageLayout>
  );
};

// ============================================================================
// Anomalies Section Component (T044)
// ============================================================================

const AnomaliesSection: React.FC = () => {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnomalies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await snapshotsApi.getAnomalies({ days: 7 });
      setAnomalies(response.anomalies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  if (error) {
    return (
      <Card className="p-4">
        <ErrorState title="Failed to load anomalies" message={error} onRetry={fetchAnomalies} />
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <AnomalyList anomalies={anomalies} loading={loading} onRefresh={fetchAnomalies} />
    </Card>
  );
};

export default InfrastructureTrendsPage;
